// src/components/ServiceCard.tsx
import React from 'react';
import { Box, Card, CardContent, Typography, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Service } from '../../../types';
import ImageViewer from '../../global/images/imageViewer';
import { formatMinutes } from '../../../time/timeUtils';

interface ServiceCardProps {
  service: Service;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service }) => {
  const theme = useTheme();
  const { title, description, time } = service;

  return (
    <Card
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[2],
        borderRadius: 3,
        overflow: 'hidden',
        width: '95%',
        margin: '0 auto',
        backgroundColor: theme.palette.background.paper,
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <ImageViewer imagePath={service.imagePath} styleType="landscape" altText="Service Image" />
      </Box>
      <CardContent
        sx={{
          textAlign: 'center',
          py: 0.9,
          px: 1.1,
          color: theme.palette.text.primary,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.6,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.15, textAlign: 'center' }}>
          {title}
        </Typography>

        <Box display="flex" alignItems="center" justifyContent="center" gap={1.2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={0.5}>
            <AccessTimeIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            <Typography variant="caption" color="text.secondary">{formatMinutes(time)}</Typography>
          </Box>
        </Box>

        {/* Clean description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            lineHeight: 1.25,
            textAlign: 'center',
            width: '100%',
            minHeight: '2.6em', // reserve space equal to two lines so heights stay consistent
          }}
        >
          {description?.trim() ? description : '\u00A0'}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default ServiceCard;
